import { LinkOutlined, PlusOutlined, SearchOutlined } from "@ant-design/icons";
import {
  Button,
  Col,
  DatePicker,
  Drawer,
  Form,
  Input,
  Row,
  Select,
  Table,
  TableColumnsType,
  Tag,
  Typography,
} from "antd";
import { debounce } from "lodash";
import React from "react";
import { useNavigate } from "react-router-dom";
import ProductionProjectOverView from "../../components/production/ProductionProjectOverView";
import { ProjectStatus } from "../../enum/common";
import {
  ICreateProductionProject,
  IProductionProject,
} from "../../models/Production";
import { IFilterResearch } from "../../models/Research";
import { IContact, ICustomer } from "../../models/Sale";
import {
  useCreateProductionProject,
  useGetProductionProject,
} from "../../query/ProductionQuery";
import { useGetContact, useGetCustomer } from "../../query/SaleQuery";
import { notify } from "../../utils/toast";

const { Text } = Typography;

const ProductionProjects = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [openDrawer, setOpenDrawer] = React.useState<boolean>(false);
  const [customerSearch, setCustomerSearch] = React.useState<string>("");
  const [customerPage, setCustomerPage] = React.useState<number>(1);
  const [contactPage, setContactPage] = React.useState<number>(1);
  const [contactSearch, setContactSearch] = React.useState<string>("");
  const [query, setQuery] = React.useState<IFilterResearch>({
    page: 1,
    limit: 10,
  });

  const { data: productionProjects, isLoading } =
    useGetProductionProject(query);
  const createProjectMutation = useCreateProductionProject();
  const { data: customerData, isLoading: isLoadingCustomer } = useGetCustomer({
    search: customerSearch,
    page: customerPage,
    limit: 5,
  });
  const { data: contactData, isLoading: isLoadingContact } = useGetContact({
    search: contactSearch,
    page: contactPage,
    limit: 5,
  });

  const [allCustomers, setAllCustomers] = React.useState<ICustomer[]>([]);
  const [allContacts, setAllContacts] = React.useState<IContact[]>([]);

  React.useEffect(() => {
    if (customerData?.data !== undefined) {
      if (customerPage === 1) {
        setAllCustomers(customerData.data);
      } else {
        setAllCustomers((prev) => [...prev, ...customerData.data]);
      }
    }
  }, [customerData, customerPage]);

  React.useEffect(() => {
    setCustomerPage(1);
  }, [customerSearch]);

  React.useEffect(() => {
    if (contactData?.data !== undefined) {
      if (contactPage === 1) {
        setAllContacts(contactData.data);
      } else {
        setAllContacts((prev) => [...prev, ...contactData.data]);
      }
    }
  }, [contactData, contactPage]);

  React.useEffect(() => {
    setContactPage(1);
  }, [contactSearch]);

  const handleCustomerScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.currentTarget;

    const isNearBottom =
      target.scrollHeight - target.scrollTop - target.clientHeight < 10;

    if (
      isNearBottom &&
      !isLoadingCustomer &&
      customerData?.pagination &&
      customerPage < customerData.pagination.totalPages
    ) {
      setCustomerPage((prev) => prev + 1);
    }
  };

  const handleContactScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.currentTarget;
    const isNearBottom =
      target.scrollHeight - target.scrollTop - target.clientHeight < 10;

    if (
      isNearBottom &&
      !isLoadingContact &&
      contactData?.pagination &&
      contactPage < contactData.pagination.totalPages
    ) {
      setContactPage((prev) => prev + 1);
    }
  };
  const debouncedCustomerSearch = React.useMemo(
    () =>
      debounce((value: string) => {
        setCustomerSearch(value || "");
      }, 300),
    [],
  );

  const debouncedContactSearch = React.useMemo(
    () =>
      debounce((value: string) => {
        setContactSearch(value || "");
      }, 300),
    [],
  );

  const debouncedSearch = React.useMemo(
    () =>
      debounce((field: keyof IFilterResearch, value: string) => {
        setQuery((prev) => ({
          ...prev,
          [field]: value || undefined,
          page: 1,
        }));
      }, 300),
    [],
  );

  const handleSearch = (field: keyof IFilterResearch) => (value: string) => {
    debouncedSearch(field, value);
  };

  const handleCreateProject = async () => {
    try {
      const values = await form.validateFields();
      const contactProject =
        values.contactProject?.map((contact: string) => {
          const [name, phone, position] = contact.split(" - ");
          return { name, phone, position };
        }) || [];

      const data: ICreateProductionProject = {
        ...values,
        contactProject: JSON.stringify(contactProject),
        requestDate: values.requestDate.format("YYYY-MM-DD"),
        startDate: values.startDate.format("YYYY-MM-DD"),
        deadline: values.deadline.format("YYYY-MM-DD"),
      };

      await createProjectMutation.mutateAsync(data);
      notify.success("Tạo dự án thành công");
      setOpenDrawer(false);
      form.resetFields();
    } catch (error) {
      notify.error("Có lỗi xảy ra khi tạo dự án");
    }
  };

  const handleStatusClick = (status: string) => {
    const statusMap: Record<string, string> = {
      new: "new",
      progress: "progress",
      done: "done",
      cancel: "cancel",
      overdue: "overdue",
    };

    setQuery((prev) => ({
      ...prev,
      status: status ? statusMap[status] : undefined,
      page: 1,
    }));
  };

  const columns: TableColumnsType<IProductionProject> = [
    {
      title: "Tên dự án",
      dataIndex: "projectName",
      key: "projectName",
      align: "center",
      render: (text) => <Text strong>{text}</Text>,
      filterDropdown: () => (
        <div className="p-4">
          <Input
            placeholder="Nhập tên dự án"
            prefix={<SearchOutlined />}
            onChange={(e) => handleSearch("projectName")(e.target.value)}
          />
        </div>
      ),
    },
    {
      title: "Mã dự án",
      dataIndex: "project",
      key: "project",
      align: "center",
      filterDropdown: () => (
        <div className="p-4">
          <Input
            placeholder="Nhập mã dự án"
            prefix={<SearchOutlined />}
            onChange={(e) => handleSearch("project")(e.target.value)}
          />
        </div>
      ),
    },
    {
      title: "Khách hàng",
      dataIndex: "customerName",
      key: "customerName",
      align: "center",
    },

    {
      title: "Yêu cầu từ",
      dataIndex: "requestFrom",
      key: "requestFrom",
      align: "center",
    },
    {
      title: "Ngày yêu cầu",
      dataIndex: "requestDate",
      key: "requestDate",
      align: "center",
      render: (date) =>
        date ? new Date(date).toLocaleDateString("vi-VN") : "-",
    },
    {
      title: "Ngày khởi chạy",
      dataIndex: "startDate",
      key: "startDate",
      align: "center",
      render: (date) =>
        date ? (
          <Tag color="green">{new Date(date).toLocaleDateString("vi-VN")}</Tag>
        ) : (
          "-"
        ),
    },
    {
      title: "Hạn hoàn thành",
      dataIndex: "deadline",
      key: "deadline",
      align: "center",
      render: (date) =>
        date ? (
          <Tag color="red">{new Date(date).toLocaleDateString("vi-VN")}</Tag>
        ) : (
          "-"
        ),
    },
    {
      title: "Trạng thái",
      dataIndex: "status",
      key: "status",
      align: "center",
      render: (status: ProjectStatus) => {
        const statusConfig = {
          [ProjectStatus.NEW]: { color: "blue", text: "Mới" },
          [ProjectStatus.IN_PROGRESS]: {
            color: "orange",
            text: "Đang thực hiện",
          },
          [ProjectStatus.DONE]: { color: "green", text: "Hoàn thành" },
          [ProjectStatus.CANCELLED]: { color: "red", text: "Đã hủy" },
        };
        const config = statusConfig[status] || {
          color: "default",
          text: status,
        };
        return <Tag color={config.color}>{config.text}</Tag>;
      },
    },
    {
      title: "Hành động",
      key: "action",
      align: "center",
      render: (_, record) => (
        <Button
          type="link"
          icon={<LinkOutlined />}
          onClick={() => navigate(`/production/project/${record.alias}`)}
        >
          Chi tiết
        </Button>
      ),
    },
  ];

  return (
    <div className="p-4">
      <div className="flex justify-end mb-4">
        <Button
          style={{ backgroundColor: "green" }}
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => setOpenDrawer(true)}
        >
          Tạo dự án mới
        </Button>
      </div>
      <div className="mb-4">
        <ProductionProjectOverView onStatusClick={handleStatusClick} />
      </div>

      <Table
        columns={columns}
        dataSource={productionProjects?.data}
        loading={isLoading}
        rowKey={(record) => record.alias}
        scroll={{ x: 1200 }}
        pagination={{
          current: query.page,
          pageSize: query.limit,
          total: productionProjects?.pagination.total,
          showSizeChanger: true,
          showTotal: (total) => `Tổng ${total} dự án`,
          onChange: (page, pageSize) => {
            setQuery((prev) => ({
              ...prev,
              page,
              limit: pageSize,
            }));
          },
        }}
      />

      <Drawer
        title="Tạo dự án sản xuất mới"
        placement="right"
        onClose={() => {
          setOpenDrawer(false);
          form.resetFields();
        }}
        open={openDrawer}
        width={1000}
        footer={
          <div className="flex justify-end gap-2">
            <Button
              onClick={() => {
                setOpenDrawer(false);
                form.resetFields();
              }}
            >
              Hủy
            </Button>
            <Button
              type="primary"
              onClick={handleCreateProject}
              loading={createProjectMutation.isPending}
            >
              Tạo dự án
            </Button>
          </div>
        }
      >
        <Form form={form} layout="vertical">
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                label="Tên dự án"
                name="projectName"
                rules={[{ required: true, message: "Vui lòng nhập tên dự án" }]}
              >
                <Input placeholder="Nhập tên dự án" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                label="Mã dự án"
                name="project"
                rules={[{ required: true, message: "Vui lòng nhập mã dự án" }]}
              >
                <Input placeholder="Nhập mã dự án" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={24}>
              <Form.Item
                label="Mô tả dự án"
                name="projectDescription"
                rules={[
                  { required: true, message: "Vui lòng nhập mô tả dự án" },
                ]}
              >
                <Input placeholder="Nhập mô tả dự án" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                label="Khách hàng"
                name="customerName"
                rules={[
                  { required: true, message: "Vui lòng chọn khách hàng" },
                ]}
              >
                <Select
                  allowClear
                  listHeight={150}
                  showSearch
                  loading={isLoadingCustomer}
                  placeholder="Tìm kiếm và chọn khách hàng"
                  onSearch={debouncedCustomerSearch}
                  onClear={() => setCustomerSearch("")}
                  filterOption={false}
                  onPopupScroll={handleCustomerScroll}
                  options={
                    allCustomers?.map((customer) => ({
                      label: customer.name,
                      value: customer.name,
                    })) || []
                  }
                  notFoundContent={
                    isLoadingCustomer
                      ? "Đang tải..."
                      : customerSearch
                        ? "Không tìm thấy khách hàng"
                        : "Nhập để tìm kiếm"
                  }
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                label="Liên hệ dự án"
                name="contactProject"
                rules={[
                  {
                    required: true,
                    message: "Vui lòng nhập thông tin liên hệ",
                  },
                ]}
              >
                <Select
                  allowClear
                  mode="multiple"
                  listHeight={150}
                  showSearch
                  loading={isLoadingContact}
                  placeholder="Tìm kiếm và chọn liên hệ"
                  onSearch={debouncedContactSearch}
                  onClear={() => setContactSearch("")}
                  onPopupScroll={handleContactScroll}
                  filterOption={false}
                  options={
                    allContacts?.map((contact) => ({
                      label: `${contact.name} - ${contact.phone} - ${contact.position}`,
                      value: `${contact.name} - ${contact.phone} - ${contact.position}`,
                    })) || []
                  }
                  notFoundContent={
                    isLoadingContact
                      ? "Đang tải..."
                      : contactSearch
                        ? "Không tìm thấy liên hệ"
                        : "Nhập để tìm kiếm"
                  }
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item label="Sale phụ trách" name="saleInCharge">
                <Input placeholder="Nhập sale phụ trách" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item label="Sale Admin phụ trách" name="saleAdInCharge">
                <Input placeholder="Nhập sale admin phụ trách" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item label="Mua hàng phụ trách" name="purchaseInCharge">
                <Input placeholder="Nhập mua hàng phụ trách" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                label="Yêu cầu từ"
                name="requestFrom"
                rules={[
                  { required: true, message: "Vui lòng nhập nguồn yêu cầu" },
                ]}
              >
                <Input placeholder="Nhập nguồn yêu cầu" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={8}>
              <Form.Item
                label="Ngày yêu cầu"
                name="requestDate"
                rules={[
                  { required: true, message: "Vui lòng chọn ngày yêu cầu" },
                ]}
              >
                <DatePicker
                  style={{ width: "100%" }}
                  placeholder="Chọn ngày yêu cầu"
                  format="DD/MM/YYYY"
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item
                label="Ngày khởi chạy"
                name="startDate"
                rules={[
                  { required: true, message: "Vui lòng chọn ngày khởi chạy" },
                ]}
              >
                <DatePicker
                  style={{ width: "100%" }}
                  placeholder="Chọn ngày khởi chạy"
                  format="DD/MM/YYYY"
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item
                label="Hạn hoàn thành"
                name="deadline"
                rules={[
                  {
                    required: true,
                    message: "Vui lòng chọn hạn hoàn thành",
                  },
                ]}
              >
                <DatePicker
                  style={{ width: "100%" }}
                  placeholder="Chọn hạn hoàn thành"
                  format="DD/MM/YYYY"
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Drawer>
    </div>
  );
};

export default ProductionProjects;
